import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { portalApi, apiError, LOGO_URL, getJoinRef } from "@/lib/api";
import { ReferrerBanner } from "@/components/ReferrerBanner";
import { PageBackground } from "@/components/PageBackground";
import { Talbiyah, GLASS, SHELL } from "@/components/Talbiyah";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Smartphone, User } from "lucide-react";

export default function PortalLogin() {
  const navigate = useNavigate();
  const [signin, setSignin] = useState({ name: "", phone: "" });
  const [signup, setSignup] = useState({ name: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [refCode] = useState(getJoinRef);

  const enter = (data) => {
    localStorage.setItem("portal_token", data.token);
    toast.success(`Assalamu Alaikum, ${data.contact.name}`);
    navigate("/portal/home");
  };

  const doSignin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await portalApi.post("/portal/login", signin);
      enter(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const doSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await portalApi.post("/portal/signup", { ...signup, ref: refCode });
      localStorage.removeItem("join_ref");
      enter(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={SHELL}>
      <PageBackground dim={0.04} />
      <Talbiyah />
      <div className={`w-full max-w-[380px] fade-in ${GLASS}`}>
        <div className="flex flex-col items-center text-center mb-6">
          <img src={LOGO_URL} alt="Bengal Umrah House" className="w-28 h-28 rounded-full object-cover ring-2 ring-gold/70 shadow-[0_0_45px_rgba(212,175,55,0.4)]" />
          <h1 className="font-serif text-2xl font-semibold text-white mt-4">Bengal Umrah House</h1>
          <p className="eyebrow text-gold">Pilgrim Portal</p>
        </div>

        <Tabs defaultValue="signin">
          <TabsList className="grid grid-cols-2 w-full bg-card/50 border border-gold/25 mb-4">
            <TabsTrigger value="signin" data-testid="portal-tab-signin" className="text-xs">Log In</TabsTrigger>
            <TabsTrigger value="signup" data-testid="portal-tab-signup" className="text-xs">Sign Up</TabsTrigger>
          </TabsList>

          <TabsContent value="signin">
            <form onSubmit={doSignin} className="space-y-4" data-testid="portal-login-form">
              <div>
                <Label className="text-xs text-emerald-100/70 flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Your name (as given at booking)</Label>
                <Input data-testid="portal-name-input" value={signin.name} onChange={(e) => setSignin({ ...signin, name: e.target.value })} placeholder="e.g. Mohammed Farhan" className="mt-1.5 bg-[#05261D]/70 border-gold/30" required />
              </div>
              <div>
                <Label className="text-xs text-emerald-100/70 flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5" /> Registered mobile number</Label>
                <Input data-testid="portal-phone-input" value={signin.phone} onChange={(e) => setSignin({ ...signin, phone: e.target.value })} placeholder="+9198XXXXXXXX" className="mt-1.5 bg-[#05261D]/70 border-gold/30" required />
              </div>
              <Button type="submit" data-testid="portal-login-btn" disabled={loading} className="w-full bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enter Portal"}
              </Button>
              <p className="text-[11px] text-emerald-100/50 text-center">Your name + mobile number is your login. No OTP needed.</p>
            </form>
          </TabsContent>

          <TabsContent value="signup">
            <form onSubmit={doSignup} className="space-y-4" data-testid="portal-signup-form">
              <ReferrerBanner code={refCode} />
              <div>
                <Label className="text-xs text-emerald-100/70 flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Full name</Label>
                <Input data-testid="portal-signup-name" value={signup.name} onChange={(e) => setSignup({ ...signup, name: e.target.value })} placeholder="e.g. Mohammed Farhan" className="mt-1.5 bg-[#05261D]/70 border-gold/30" required />
              </div>
              <div>
                <Label className="text-xs text-emerald-100/70 flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5" /> Mobile number</Label>
                <Input data-testid="portal-signup-phone" value={signup.phone} onChange={(e) => setSignup({ ...signup, phone: e.target.value })} placeholder="+9198XXXXXXXX" className="mt-1.5 bg-[#05261D]/70 border-gold/30" required />
              </div>
              <Button type="submit" data-testid="portal-signup-btn" disabled={loading} className="w-full bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create My Account"}
              </Button>
              <p className="text-[11px] text-emerald-100/50 text-center">Register your name + mobile to upload documents and manage your Umrah booking.</p>
            </form>
          </TabsContent>
        </Tabs>

        <p className="text-center mt-5">
          <a href="/showcase" data-testid="portal-showcase-link" className="text-[11px] text-gold/80 hover:text-gold underline underline-offset-2">Browse our Umrah tour gallery (no login needed)</a>
        </p>
      </div>
    </div>
  );
}
